import { useState, useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { pageVariants, useMotionVariants } from '../motion/motionTokens'
import { Header } from './Header'
import { Sidebar } from './Sidebar'
import { BottomNav } from './BottomNav'
import { NotificationSystem } from '../components/NotificationSystem'
import { RewardAlert } from '../components/RewardAlert'
import { LevelUpModal } from '../components/LevelUpModal'
import { ConstructionCompleteModal } from '../components/ConstructionCompleteModal'
import { StoryBeatModal } from '../components/StoryBeatModal'
import { MusicMiniPlayer } from '../components/MusicMiniPlayer'
import { ReportModal } from '../components/ReportModal'
import { TutorialOverlay } from '../components/TutorialOverlay'
import { EventsManager } from '../components/WeatherEvents'
import { CelebrationManager } from '../celebrations/CelebrationManager'
import { GameOverScreen, VictoryScreen } from '../components/GameResultScreens'
import { useAchievementTracking } from '@/hooks/useAchievementTracking'
import { useGameAudio } from '@/hooks/useGameAudio'
import { useAudioStore } from '@/store/audioStore'

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  return isMobile
}

export function GameLayout() {
  const isMobile = useIsMobile()
  const location = useLocation()
  const variants = useMotionVariants(pageVariants)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const musicBarHidden = useAudioStore(s => s.miniPlayerHidden)

  useAchievementTracking()
  useGameAudio()

  // Auto-collapse sidebar on smaller screens
  useEffect(() => {
    const check = () => setSidebarCollapsed(window.innerWidth < 1024)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const sidebarWidth = isMobile
    ? '0px'
    : sidebarCollapsed
      ? 'var(--sidebar-collapsed)'
      : 'var(--sidebar-width)'

  return (
    <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <Header onToggleSidebar={() => setSidebarCollapsed(c => !c)} isMobile={isMobile} />

      {!isMobile && <Sidebar collapsed={sidebarCollapsed} />}

      <main
        style={{
          marginTop: 'var(--header-height)',
          marginLeft: isMobile ? 0 : sidebarWidth,
          // room for the bottom nav + the now-playing music bar on mobile
          // (the music bar height is only reserved while the player is shown)
          marginBottom: isMobile
            ? `calc(var(--bottom-nav-height) + var(--safe-area-bottom)${musicBarHidden ? '' : ' + var(--music-bar-height)'})`
            : 0,
          flex: 1,
          overflow: 'auto',
          transition: 'margin-left 0.2s ease',
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            variants={variants}
            initial="initial"
            animate="enter"
            exit="exit"
            style={{ minHeight: '100%' }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {isMobile && <BottomNav />}
      {/* Desktop shows the player in the sidebar footer; mobile gets a slim
          now-playing bar pinned above the bottom nav */}
      {isMobile && <MusicMiniPlayer variant="bar" />}

      {/* Global overlays */}
      <RewardAlert isMobile={isMobile} />
      <NotificationSystem />
      <StoryBeatModal />
      <LevelUpModal />
      <ConstructionCompleteModal />
      <ReportModal />
      <TutorialOverlay />
      <EventsManager />
      <CelebrationManager />
      <GameOverScreen />
      <VictoryScreen />
    </div>
  )
}
